import { useState } from "react";
import AnimateOnScroll from "../AnimateOnScroll";
import { Mail, User, MessageSquare, MapPin, Clock, ExternalLink, Loader2 } from "lucide-react";
import { siteContent } from "@/data/site-content";
import { handleApiError } from "@/lib/errors";
import { trackContactSubmit } from "@/lib/analytics";

interface ContactFormSectionProps {
  heading: string;
  body: string;
}

const ADDRESS_FULL = "Andreas Nilsens vei 19, 3040 Drammen";
const MAPS_QUERY = encodeURIComponent(ADDRESS_FULL);

const ContactFormSection = ({ heading, body }: ContactFormSectionProps) => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { contact } = siteContent.globals;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `Kunne ikke sende (${res.status})`);
      }
      trackContactSubmit();
      setSubmitted(true);
    } catch (err) {
      handleApiError(err, {
        source: "ContactFormSection.submit",
        fallbackMessage: "Kunne ikke sende meldingen. Prøv igjen, eller send oss en e-post direkte.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
      <div className="grid gap-12 lg:grid-cols-2">
        {/* Left: Contact form */}
        <AnimateOnScroll>
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">{heading}</h2>
              <p className="mt-4 text-lg text-muted-foreground font-body">{body}</p>
            </div>
            {submitted ? (
              <div className="rounded-2xl bg-secondary p-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Mail className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold">Takk for din melding!</h3>
                <p className="mt-2 text-muted-foreground font-body">
                  Vi svarer deg så raskt vi kan med varme og engasjement.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl bg-card border border-border p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="mb-2 flex items-center gap-2 text-sm font-medium font-body"
                    >
                      <User className="h-4 w-4 text-primary" aria-hidden="true" />
                      Navn
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full rounded-xl border border-input bg-background px-4 py-3 font-body text-foreground transition-all focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Ditt navn"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="mb-2 flex items-center gap-2 text-sm font-medium font-body"
                    >
                      <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
                      E-post
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full rounded-xl border border-input bg-background px-4 py-3 font-body text-foreground transition-all focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="din@epost.no"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="mb-2 flex items-center gap-2 text-sm font-medium font-body"
                    >
                      <MessageSquare className="h-4 w-4 text-primary" aria-hidden="true" />
                      Melding
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full resize-none rounded-xl border border-input bg-background px-4 py-3 font-body text-foreground transition-all focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Skriv din melding her..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 font-body font-semibold text-primary-foreground shadow-lg transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sender …
                      </>
                    ) : (
                      "Send melding"
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </AnimateOnScroll>

        {/* Right: Contact info + map */}
        <AnimateOnScroll delay="delay-200">
          <div className="space-y-8">
            <h2 className="text-3xl font-bold md:text-4xl">Finn oss</h2>

            <div className="space-y-4">
              {contact.address && (
                <div className="flex items-start gap-3 text-muted-foreground font-body">
                  <MapPin className="h-5 w-5 shrink-0 mt-0.5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Adresse</p>
                    <p className="text-sm">{contact.address}</p>
                  </div>
                </div>
              )}
              {contact.openingHours && (
                <div className="flex items-start gap-3 text-muted-foreground font-body">
                  <Clock className="h-5 w-5 shrink-0 mt-0.5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">Åpningstider</p>
                    <p className="text-sm">{contact.openingHours}</p>
                  </div>
                </div>
              )}
              {contact.email && (
                <div className="flex items-start gap-3 text-muted-foreground font-body">
                  <Mail className="h-5 w-5 shrink-0 mt-0.5 text-primary" />
                  <div>
                    <p className="font-medium text-foreground">E-post</p>
                    <a href={`mailto:${contact.email}`} className="text-sm text-primary hover:text-primary/80 transition-colors">
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Google Maps embed — search-URL slik at vi alltid peker på riktig adresse */}
            <div className="overflow-hidden rounded-2xl border border-border shadow-sm">
              <iframe
                title="Håp Naturlab lokasjon"
                src={`https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`}
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-secondary px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary/80 font-body"
              >
                <ExternalLink className="h-4 w-4" />
                Åpne i Google Maps
              </a>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ContactFormSection;
